import { styled } from 'styled-components'

import { RyuBreak, RyuFlex, RyuLoadingSpinner, RyuText } from '@ramp/ryu'

import { UsernameSignUpFormFields } from './UsernameSignUpFormFields'
import { KenForm } from '../ken/KenForm/KenForm'
import { KenFormSubmitButton } from '../ken/KenForm/KenFormSubmitButton'
import { SignUpFormFields, useSignUp } from '~/src/hooks/useSignUp'
import { UserAlreadyExists } from '../UserAlreadyExists'
import { KenFieldCheckboxGroup } from '~/src/components/ken/KenForm/KenFieldCheckboxGroup'
import { getAccountsReceivableInterestOptions } from '~/src/utils/accountsReceivableInterest'

export type PossibleInterestedProducts =
  | 'interested_in_corporate_cards'
  | 'interested_in_bill_payments'
  | 'interested_in_procurement'
  | 'interested_in_treasury'
  | 'interested_in_accounts_receivable'
  | 'interested_in_stack'
  | 'interested_in_token_spend_management'

export const INTERESTED_PRODUCTS_LABEL_MAP: Record<PossibleInterestedProducts, string> = {
  interested_in_corporate_cards: 'Corporate Cards and Expense Management',
  interested_in_bill_payments: 'Accounts Payable',
  interested_in_procurement: 'Procurement',
  interested_in_treasury: 'Banking',
  interested_in_accounts_receivable: 'Accounts Receivable',
  interested_in_stack: 'Accounting Automation',
  interested_in_token_spend_management: 'AI Spend Management',
}

export function UsernameSignUp(params: any) {
  const { leadEmail, hideUkUnavailableProducts = false } = params
  const {
    leadCheckForExistingIdentityIsLoading,
    allowPersonalEmails,
    form,
    leadEmailMatchesExistingIdentity,
    existingBusinessError,
    onSubmit,
    validateEmail,
  } = useSignUp(params)

  if (leadCheckForExistingIdentityIsLoading) {
    return (
      <RyuFlex justifyContent='center' alignItems='center'>
        <RyuLoadingSpinner size='xl' />
      </RyuFlex>
    )
  }

  if (leadEmailMatchesExistingIdentity && leadEmail) {
    return <UserAlreadyExists email={leadEmail} />
  }

  return (
    <FullWidth>
      <KenForm
        form={form}
        render={({ formProps }) => (
          <form {...formProps} noValidate={true}>
            <UsernameSignUpFormFields
              form={form}
              allowPersonalEmails={allowPersonalEmails}
              validateEmail={validateEmail}
              existingBusinessError={existingBusinessError}
            />

            <RyuBreak size='s' />

            <RyuText.span size='s' color='hushed'>
              Products you’re interested in
            </RyuText.span>

            <RyuBreak size='xs' />

            <KenFieldCheckboxGroup<
              SignUpFormFields,
              'interested_in_products',
              PossibleInterestedProducts
            >
              name='interested_in_products'
              gapSize='xs'
              rules={{ required: 'Select at least one option' }}
              caption='Don’t worry. This is not a commitment to any product.'
              items={[
                ...(hideUkUnavailableProducts
                  ? (['interested_in_corporate_cards', 'interested_in_bill_payments'] as const)
                  : ([
                      'interested_in_corporate_cards',
                      'interested_in_bill_payments',
                      'interested_in_procurement',
                      'interested_in_treasury',
                    ] as const)),
                ...getAccountsReceivableInterestOptions().map(({ value }) => value),
                'interested_in_stack',
                'interested_in_token_spend_management',
              ]}
              parseItem={(value) => ({
                label: INTERESTED_PRODUCTS_LABEL_MAP[value],
                value,
                key: value,
                variant: 'lean',
                iconSide: 'right',
              })}
            />

            <RyuBreak size='xl' />

            <KenFormSubmitButton color='primary' variant='prime' fullWidth={true} _textAlign='center'>
              Get started
            </KenFormSubmitButton>
          </form>
        )}
        onSubmit={onSubmit}
      />
    </FullWidth>
  )
}

const FullWidth = styled.div`
  width: 100%;
`
